import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ComplianceScoreRing } from '@/components/ComplianceScoreRing';
import { VendorStatusPill } from '@/components/VendorStatusPill';
import { getDashboardStats } from '@/lib/api';
import api from '@/lib/api';
import { useTimezone, formatDateTime } from '@/lib/timezone';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  Users, AlertTriangle, FileText, CalendarClock,
  ArrowUpRight, TrendingUp, ChevronRight, Upload, Eye, Link2, RefreshCw, Activity,
  DollarSign, CreditCard, Shield, AlertCircle, Clock
} from 'lucide-react';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [billing, setBilling] = useState(null);
  const [flags, setFlags] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const tz = useTimezone();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Check for past_due subscription status
    if (user?.subscription_status === 'past_due') {
      setShowPaymentFailedModal(true);
    }
    
    // Check for Limited Demo trial expiration and days remaining
    if (user?.plan_tier === 'limited_demo') {
      const checkTrial = async () => {
        try {
          const { data: company } = await api.get(`/companies/${user.company_id}`);
          if (company.trial_expires_at) {
            const expiresAt = new Date(company.trial_expires_at);
            const now = new Date();
            const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
            setDaysRemaining(daysLeft);
            
            if (daysLeft <= 0) {
              setShowTrialExpiredModal(true);
            }
          }
        } catch (err) {
          console.error('Failed to check trial status:', err);
        }
      };
      checkTrial();
    }
    
    // Check subscription status with backend (reconciliation)
    const checkSubscription = async () => {
      try {
        const { data } = await api.get('/stripe/subscription-details');
        if (data.subscription_status === 'active' && user?.subscription_status !== 'active') {
          // Subscription is active in Stripe but user context is stale - refresh
          console.log('Subscription active - refreshing user...');
          window.location.reload(); // Force refresh to get updated JWT
        }
      } catch (err) {
        console.error('Failed to check subscription:', err);
      }
    };
    
    if (user && user.subscription_status !== 'active') {
      checkSubscription();
    }
    
    const load = async () => {
      try {
        const [statsRes, activityRes, billingRes, flagsRes, analyticsRes] = await Promise.all([
          getDashboardStats(),
          api.get('/dashboard/activity', { params: { limit: 15 } }).catch(() => ({ data: { items: [] } })),
          api.get('/billing/summary').catch(() => ({ data: null })),
          api.get('/dashboard/compliance-flags').catch(() => ({ data: null })),
          api.get('/dashboard/analytics').catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setBilling(billingRes.data);
        setFlags(flagsRes.data);
        setAnalytics(analyticsRes.data);
        setActivity(activityRes.data?.items || []);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Skeleton className="lg:col-span-7 h-80 rounded-xl" />
          <Skeleton className="lg:col-span-5 h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  const kpis = [
    {
      label: 'Total Vendors', value: stats?.total_vendors || 0, icon: Users,
      sub: `${stats?.compliant_vendors || 0} compliant`, color: 'text-primary'
    },
    {
      label: 'Expiring / Expired', value: (stats?.expired_vendors || 0) + (stats?.warning_vendors || 0), icon: AlertTriangle,
      sub: `${stats?.expired_vendors || 0} expired`, color: 'text-destructive'
    },
    {
      label: 'Docs Processing', value: stats?.documents_processing || 0, icon: FileText,
      sub: `${stats?.total_documents || 0} total`, color: 'text-[hsl(197,78%,24%)]'
    },
    {
      label: 'Upcoming Renewals', value: stats?.upcoming_renewals || 0, icon: CalendarClock,
      sub: 'Next 60 days', color: 'text-[hsl(36,90%,50%)]'
    }
  ];

  const pieData = [
    { name: 'Compliant', value: stats?.compliant_vendors || 0, color: 'hsl(142, 56%, 36%)' },
    { name: 'Warning', value: stats?.warning_vendors || 0, color: 'hsl(36, 90%, 50%)' },
    { name: 'Expired', value: stats?.expired_vendors || 0, color: 'hsl(0, 72%, 52%)' },
    { name: 'Pending', value: stats?.pending_vendors || 0, color: 'hsl(197, 78%, 50%)' },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Dashboard</h1>
          <p className="text-sm text-muted-foreground">Portfolio compliance overview</p>
        </div>
        <Button data-testid="dashboard-add-vendor-button" onClick={() => navigate('/vendors')}>
          <Users className="mr-2 h-4 w-4" /> View Vendors
        </Button>
      </div>

      {/* Onboarding Checklist (shows only for new accounts) */}
      <OnboardingChecklist />


      {/* Trial Days Remaining Banner */}
      {user?.plan_tier === 'limited_demo' && daysRemaining !== null && daysRemaining > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm">
                  Free Trial: {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
                </p>
                <p className="text-xs text-muted-foreground">
                  Limited to 5 vendors with documents • Upgrade anytime for unlimited access
                </p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/pricing')}>
              Upgrade Now
            </Button>
          </div>
        </div>
      )}


      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="rounded-xl border border-border shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-pop)] hover:-translate-y-[1px] transition-shadow duration-200 cursor-pointer">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{kpi.label}</p>
                  <p className="text-3xl font-semibold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{kpi.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                </div>
                <div className={`p-2 rounded-lg bg-muted ${kpi.color}`}>
                  <kpi.icon className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Compliance Trend */}
        <Card className="lg:col-span-7 rounded-xl border border-border shadow-[var(--shadow-soft)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <TrendingUp className="inline mr-2 h-4 w-4" />
              Compliance Trend
            </CardTitle>
            <Badge variant="secondary" className="text-xs">12 months</Badge>
          </CardHeader>
          <CardContent>
            <div data-testid="dashboard-compliance-trend-chart" className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.compliance_trend || []}>
                  <defs>
                    <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(186, 64%, 36%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(186, 64%, 36%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <RTooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: 12
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(186, 64%, 36%)"
                    fill="url(#scoreGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Score + Distribution */}
        <Card className="lg:col-span-5 rounded-xl border border-border shadow-[var(--shadow-soft)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Portfolio Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div data-testid="dashboard-portfolio-score" className="flex items-center gap-6">
              <ComplianceScoreRing score={Math.round(stats?.portfolio_compliance_score || 0)} label="Portfolio" />
              <div className="flex-1">
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3}>
                        {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <RTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pieData.map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      {d.name} ({d.value})
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts & Risky Vendors Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Alerts */}
        <Card data-testid="dashboard-alerts-panel" className="lg:col-span-5 rounded-xl border border-border shadow-[var(--shadow-soft)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <AlertTriangle className="inline mr-2 h-4 w-4" />
              Alerts
            </CardTitle>
            <Badge variant="destructive" className="text-xs">{stats?.recent_alerts?.length || 0}</Badge>
          </CardHeader>
          <CardContent>
            {stats?.recent_alerts?.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-auto scrollbar-thin">
                {stats.recent_alerts.map((alert, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors duration-150 cursor-pointer"
                    onClick={() => navigate(`/vendors/${alert.vendor_id}`)}
                  >
                    <div className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${alert.type === 'expired' ? 'bg-destructive' : 'bg-[hsl(36,90%,50%)]'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{alert.message}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{alert.date ? new Date(alert.date).toLocaleDateString() : ''}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">No active alerts</p>
            )}
          </CardContent>
        </Card>

        {/* Top Risky Vendors */}
        <Card data-testid="dashboard-top-risky-vendors-table" className="lg:col-span-7 rounded-xl border border-border shadow-[var(--shadow-soft)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Top Risky Vendors
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/vendors?status=expired')}>
              View all <ArrowUpRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {stats?.top_risky_vendors?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_risky_vendors.map((v, i) => (
                  <div key={i}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
                    onClick={() => navigate(`/vendors/${v.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium">
                        {v.name?.[0] || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{v.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{v.vendor_type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {v.compliance_score}%
                      </span>
                      <VendorStatusPill status={v.status} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">All vendors are compliant</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Estimated Monthly Bill Widget */}
      {billing && (
        <Card data-testid="dashboard-billing-widget" className="rounded-xl border border-border shadow-[var(--shadow-soft)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <CreditCard className="inline mr-2 h-4 w-4" />
              Estimated Monthly Bill
            </CardTitle>
            <Badge variant="secondary" className="text-xs capitalize">{billing.plan_tier} Plan</Badge>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">Base Fee</p>
                <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>${billing.base_fee}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">PM-Paid Vendors ({billing.pm_paid_vendors})</p>
                <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>${billing.pm_vendor_fee}</p>
                <p className="text-xs text-muted-foreground">${billing.per_vendor_rate}/vendor</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">Vendor-Paid ({billing.vendor_paid_vendors})</p>
                <p className="text-xl font-semibold tabular-nums text-[hsl(142,56%,36%)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$0</p>
                <p className="text-xs text-muted-foreground">Billed to vendors</p>
              </div>
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-xs text-muted-foreground">Total Monthly</p>
                <p className="text-xl font-bold tabular-nums text-primary" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>${billing.total_estimated_monthly}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Compliance Flags + Predictive Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Compliance Flags */}
        {flags && flags.total > 0 && (
          <Card data-testid="dashboard-compliance-flags" className="lg:col-span-5 rounded-xl border border-border shadow-[var(--shadow-soft)]">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <AlertCircle className="inline mr-2 h-4 w-4" /> Compliance Flags
              </CardTitle>
              <Badge variant="destructive" className="text-xs">{flags.total}</Badge>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3 flex-wrap">
                {Object.entries(flags.by_type || {}).map(([type, count]) => (
                  <Badge key={type} variant="outline" className="text-[10px] capitalize">{type}: {count}</Badge>
                ))}
              </div>
              <ScrollArea className="max-h-48">
                <div className="space-y-1.5">
                  {flags.flags.slice(0, 10).map((f, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors duration-100" onClick={() => navigate(`/vendors/${f.vendor_id}`)}>
                      <AlertTriangle className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${f.severity === 'high' ? 'text-destructive' : 'text-[hsl(36,90%,50%)]'}`} />
                      <div className="min-w-0">
                        <p className="text-xs"><span className="font-medium">{f.vendor_name}</span> — {f.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Predictive Analytics */}
        {analytics && (
          <Card data-testid="dashboard-predictive-analytics" className={`${flags && flags.total > 0 ? 'lg:col-span-7' : 'lg:col-span-12'} rounded-xl border border-border shadow-[var(--shadow-soft)]`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <TrendingUp className="inline mr-2 h-4 w-4" /> Predictive Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="p-2.5 rounded-lg bg-muted/50 text-center">
                  <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.renewals_expiring}</p>
                  <p className="text-[10px] text-muted-foreground">Expiring (60d)</p>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/50 text-center">
                  <p className="text-lg font-bold tabular-nums text-destructive" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.renewals_critical}</p>
                  <p className="text-[10px] text-muted-foreground">Critical</p>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/50 text-center">
                  <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.coverage_issues_count}</p>
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-[10px] text-muted-foreground">Coverage Gaps</p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-muted-foreground/40 text-[8px] text-muted-foreground cursor-help">i</span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                          <p className="font-medium mb-1">What are Coverage Gaps?</p>
                          <p>The number of individual coverage fields across all vendor-property combinations where the actual coverage is below the property's minimum requirement.</p>
                          <p className="mt-1 text-muted-foreground">Example: If a vendor has $500K General Liability but a property requires $1M, that counts as 1 gap. A vendor at 3 properties each missing 2 fields = 6 gaps.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <div className="p-2.5 rounded-lg bg-muted/50 text-center">
                  <p className="text-lg font-bold tabular-nums text-[hsl(36,90%,50%)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.risk_vendors?.length || 0}</p>
                  <p className="text-[10px] text-muted-foreground">At-Risk Vendors</p>
                </div>
              </div>
              {analytics.risk_vendors?.length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-2">High-Risk Vendors (likely to lapse)</p>
                  <div className="space-y-1.5">
                    {analytics.risk_vendors.slice(0, 5).map((v, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors duration-100" onClick={() => navigate(`/vendors/${v.vendor_id}`)}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{v.vendor_name}</span>
                          <Badge variant="outline" className="text-[9px] capitalize">{v.behavior}</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs tabular-nums">{v.score}%</span>
                          <span className="text-[10px] text-muted-foreground">{v.response_rate}% resp.</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {analytics.upcoming_renewals?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium mb-2">Upcoming Renewals</p>
                  <div className="space-y-1">
                    {analytics.upcoming_renewals.slice(0, 4).map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/30">
                        <span>{r.vendor_name} — {r.document_type?.toUpperCase()}</span>
                        <Badge variant={r.urgency === 'critical' ? 'destructive' : r.urgency === 'high' ? 'destructive' : 'secondary'} className="text-[9px]">{r.days_until_expiry}d left</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>



      {/* Recent Vendor Activity */}
      <Card data-testid="dashboard-recent-activity" className="rounded-xl border border-border shadow-[var(--shadow-soft)]">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Activity className="inline mr-2 h-4 w-4" />
            Recent Vendor Activity
          </CardTitle>
          <Badge variant="secondary" className="text-xs">{activity.length} events</Badge>
        </CardHeader>
        <CardContent>
          {activity.length > 0 ? (
            <ScrollArea className="max-h-72">
              <div className="space-y-2">
                {activity.map((item, i) => {
                  const iconMap = { upload: Upload, eye: Eye, link: Link2, refresh: RefreshCw };
                  const Icon = iconMap[item.icon] || FileText;
                  const colorMap = { upload: 'text-[hsl(142,56%,36%)] bg-[hsl(142_56%_92%)]', access: 'text-[hsl(197,78%,24%)] bg-[hsl(197_80%_92%)]', link: 'text-[hsl(36,90%,50%)] bg-[hsl(36_100%_92%)]' };
                  const color = colorMap[item.activity_type] || 'text-muted-foreground bg-muted';

                  return (
                    <div key={i}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
                      onClick={() => item.vendor_id ? navigate(`/vendors/${item.vendor_id}`) : null}
                    >
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className="font-medium">{item.vendor_name}</span>
                          <span className="text-muted-foreground"> — {item.description}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.created_at ? formatDateTime(item.created_at, tz) : ''}
                          {item.vendor_type ? ` · ${item.vendor_type}` : ''}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className="py-8 text-center">
              <Activity className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">No vendor portal activity yet</p>
              <p className="text-xs text-muted-foreground mt-1">Activity will appear when vendors access their portal or upload documents</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Failed Modal */}
      <Dialog open={showPaymentFailedModal} onOpenChange={setShowPaymentFailedModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Payment Unsuccessful
            </DialogTitle>
            <DialogDescription>
              We were unable to process your recent payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm font-medium mb-2">Your account access is currently restricted</p>
              <p className="text-sm text-muted-foreground">
                To restore full access to your dashboard and vendor compliance tools, please update your payment method.
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">How to update your payment method:</p>
              <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground">
                <li>Click "Update Payment Method" below</li>
                <li>You'll be taken to your Profile page</li>
                <li>Click "Manage Payment Method"</li>
                <li>Update your credit card information</li>
                <li>Your access will be restored immediately after payment succeeds</li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentFailedModal(false)}>
              Dismiss
            </Button>
            <Button onClick={() => navigate('/profile')} data-testid="payment-failed-update-method">
              <CreditCard className="mr-2 h-4 w-4" />
              Update Payment Method
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial Expired Modal */}
      <Dialog open={showTrialExpiredModal} onOpenChange={() => {}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              30-Day Trial Expired
            </DialogTitle>
            <DialogDescription>
              Your free trial of VendorSuite has ended. Upgrade to continue managing vendor compliance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm font-medium mb-2">What you experienced during your trial:</p>
              <ul className="text-xs space-y-1 text-muted-foreground">
                <li>✓ AI-powered document extraction</li>
                <li>✓ Automated compliance scoring</li>
                <li>✓ Vendor portal with magic links</li>
                <li>✓ Auto-chase reminders</li>
                <li>✓ Real-time compliance dashboard</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Choose a plan to continue:</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded-lg p-3 text-center">
                  <p className="font-semibold text-sm">Professional</p>
                  <p className="text-lg font-bold">$249<span className="text-xs">/mo</span></p>
                </div>
                <div className="border border-primary rounded-lg p-3 text-center bg-primary/5">
                  <p className="font-semibold text-sm">Enterprise</p>
                  <p className="text-lg font-bold">$599<span className="text-xs">/mo</span></p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => navigate('/pricing')}>
              View Plans & Upgrade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
